import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import {CredentialManager, DeviceManager, LanguageManager} from '@yosmanyga/base';
import {decorate, inject, injectable} from '@yosmanyga/di';

const devApiBase = 'http://localhost:80'
// const devApiBase = 'http://192.168.0.11:80'

const testingApiBase = 'http://laberinto_api_nginx:80'
const prodApiBase = 'https://api.laberinto.yosmy.com';

class AxiosInstance {
    private apiBase!: string;
    private headers!: object;

    private instance!: typeof axios;

    constructor(
        private env: string,
        private platform: string,
        private deviceManager: DeviceManager,
        private credentialManager: CredentialManager,
        private languageManager: LanguageManager
    ) {
        // Client by default
        this.fromClient();
    }

    fromClient = () => {
        this.apiBase = this.env === 'test'
            ? testingApiBase 
            : (
                this.env === 'dev' ? devApiBase : prodApiBase
            );

        return this;
    };

    withPlatform = () => {
        this.headers = {
            ...this.headers,
            Platform: this.platform,
        };

        return this;
    }

    withAuthentication = () => {
        this.headers = {
            ...this.headers,
            Authorization: this.credentialManager.get().token,
        };

        return this;
    };

    withDevice = () => {
        this.headers = {
            ...this.headers,
            Device: this.deviceManager.get(),
        };

        return this;
    };

    withLanguage = () => {
        this.headers = {
            ...this.headers,
            Language: this.languageManager.get(),
        };

        return this;
    };

    get = <T = unknown, R = AxiosResponse<T>, D = unknown>(
        url: string,
        config?: AxiosRequestConfig<D>
    ): Promise<R> =>
        this.instance
            ? this.instance.get(url, config)
            : this.instantiate().get(url, config);

    post = <T = unknown, R = AxiosResponse<T>, D = unknown>(
        url: string,
        data?: D,
        config?: AxiosRequestConfig<D>
    ): Promise<R> =>
        this.instance
            ? this.instance.post(url, data, config)
            : this.instantiate().post(url, data, config);

    private instantiate = () => {
        return axios.create({
            baseURL: this.apiBase,
            headers: {
                Game: 'fortaleza',
                ...this.headers,
            },
            responseType: 'json',
            transformResponse: (response) => {
                if (!response) {
                    return response;
                }

                try {
                    return JSON.parse(response);
                } catch (e) {
                    console.error(e);
                }
            },
        });
    };
}

decorate(injectable(), AxiosInstance);
decorate(inject('ENV'), AxiosInstance, 0);
decorate(inject('PLATFORM'), AxiosInstance, 1);
decorate(inject(DeviceManager), AxiosInstance, 2);
decorate(inject(CredentialManager), AxiosInstance, 3);
decorate(inject(LanguageManager), AxiosInstance, 4);

export {AxiosInstance};
